import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/dash/Common";
import { Pill } from "@/components/Badges";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { downloadReport, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";

const REPORTS = [
  ["siswa", "Data siswa & progres"],
  ["nilai", "Hasil ujian"],
  ["absensi", "Absensi"],
  ["pembayaran", "Pembayaran", true],
];

export function CsvButtons() {
  const { user } = useAuth();
  const dl = (k) => downloadReport(k).then(() => toast.success("Laporan diunduh")).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex flex-wrap gap-2">
      {REPORTS.filter(([, , adminOnly]) => !adminOnly || user.role === "admin").map(([k, l]) => (
        <Button key={k} variant="outline" size="sm" onClick={() => dl(k)} data-testid={`csv-download-${k}`}><Download className="h-4 w-4 mr-1" />{l}</Button>
      ))}
    </div>
  );
}

function AttemptsTable() {
  const { data } = useApi("/admin/attempts");
  const [exam, setExam] = useState("");
  const exams = useMemo(() => [...new Set((data || []).map((a) => a.exam_title))], [data]);
  const rows = (data || []).filter((a) => !exam || a.exam_title === exam);
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="p-4 border-b border-slate-200">
        <select className="h-9 rounded-md border border-input px-3 text-sm" value={exam} onChange={(e) => setExam(e.target.value)} data-testid="results-exam-filter">
          <option value="">Semua ujian</option>
          {exams.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Siswa</TableHead><TableHead>Ujian</TableHead><TableHead>Benar</TableHead><TableHead>Nilai</TableHead><TableHead>Waktu</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-slate-500">Belum ada hasil ujian</TableCell></TableRow>}
            {rows.map((a) => (
              <TableRow key={a.id} data-testid={`attempt-row-${a.id}`}>
                <TableCell className="font-medium">{a.user_name}</TableCell>
                <TableCell>{a.exam_title}</TableCell>
                <TableCell>{a.correct}/{a.total}</TableCell>
                <TableCell><span className={`font-mono font-bold ${a.score >= 70 ? "text-emerald-600" : "text-red-600"}`}>{a.score}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{fmtDateTime(a.submitted_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ProgressTable() {
  const { data } = useApi("/admin/progress");
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Siswa</TableHead><TableHead>Paket</TableHead><TableHead>Masa aktif</TableHead><TableHead className="w-48">Progres materi</TableHead><TableHead>Ujian</TableHead><TableHead>Rata-rata</TableHead><TableHead>Hadir</TableHead></TableRow></TableHeader>
        <TableBody>
          {(data || []).length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-slate-500">Belum ada siswa</TableCell></TableRow>}
          {(data || []).map((r) => (
            <TableRow key={r.id} data-testid={`progress-row-${r.id}`}>
              <TableCell><p className="font-medium">{r.name}</p><p className="text-xs text-slate-500">{r.email}</p></TableCell>
              <TableCell>{r.package}</TableCell>
              <TableCell><p className="text-xs">{fmtDate(r.package_expires_at)}</p><Pill tone={r.package_active ? "green" : "red"}>{r.package_active ? "Aktif" : "Tidak aktif"}</Pill></TableCell>
              <TableCell><div className="flex items-center gap-2"><Progress value={r.progress_pct} className="h-1.5" /><span className="text-xs font-semibold">{r.progress_pct}%</span></div></TableCell>
              <TableCell>{r.exams_taken}</TableCell>
              <TableCell className="font-mono font-bold">{r.avg_score ?? "-"}</TableCell>
              <TableCell>{r.hadir}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ResultsPage() {
  return (
    <div data-testid="admin-results-page">
      <PageHeader eyebrow="Laporan" title="Hasil ujian & progres siswa" description="Pantau nilai dan progres, lalu ekspor laporan CSV." actions={<CsvButtons />} />
      <Tabs defaultValue="hasil">
        <TabsList><TabsTrigger value="hasil" data-testid="tab-results">Hasil Ujian</TabsTrigger><TabsTrigger value="progres" data-testid="tab-progress">Progres Siswa</TabsTrigger></TabsList>
        <TabsContent value="hasil" className="mt-4"><AttemptsTable /></TabsContent>
        <TabsContent value="progres" className="mt-4"><ProgressTable /></TabsContent>
      </Tabs>
    </div>
  );
}
